import { useState } from "react";
import { useAuth } from "@/lib/auth";
import NetWorthPulse from "@/components/uc/NetWorthPulse";
import MandateDrawer from "@/components/uc/MandateDrawer";
import GlobalLogTicker from "@/components/uc/GlobalLogTicker";
import VentureCockpit from "@/components/uc/VentureCockpit";
import AgentBoardroom from "@/components/uc/AgentBoardroom";
import AutoPilotPanel from "@/components/uc/AutoPilotPanel";
import PrivateVault from "@/components/uc/PrivateVault";
import AlertsCenter from "@/components/uc/AlertsCenter";
import SettingsDrawer from "@/components/uc/SettingsDrawer";
import CommandBriefing from "@/components/uc/CommandBriefing";
import TaskBoard from "@/components/uc/TaskBoard";
import WarRoom from "@/components/uc/WarRoom";
import FixPriorityQueue from "@/components/uc/FixPriorityQueue";
import BattlePlanHistory from "@/components/uc/BattlePlanHistory";
import IntelligenceCore from "@/components/uc/IntelligenceCore";
import StatusHeatmap from "@/components/uc/StatusHeatmap";
import DraftingTable from "@/components/uc/DraftingTable";
import LeadConsole from "@/components/uc/LeadConsole";
import CommandWallet from "@/components/uc/CommandWallet";
import ActionHub from "@/components/uc/ActionHub";
import InboxMonitor from "@/components/uc/InboxMonitor";
import TwilioWire from "@/components/uc/TwilioWire";
import CommandTerminal from "@/components/uc/CommandTerminal";
import RevenueTerminal from "@/components/uc/RevenueTerminal";
import RevenueWallet from "@/components/uc/RevenueWallet";
import ExecutionConsole from "@/components/uc/ExecutionConsole";
import BridgeHandshake from "@/components/uc/BridgeHandshake";
import CreditLedgerPanel from "@/components/uc/CreditLedgerPanel";
import WorkRateMonitor from "@/components/uc/WorkRateMonitor";
import { BrandFooter } from "@/components/uc/BrandFooter";
import { UmbrellaAide } from "@/components/uc/UmbrellaAide";
import Onboarding from "@/components/uc/Onboarding";
import { OracleVoice } from "@/components/uc/OracleVoice";
import { VentureHealthMap } from "@/components/uc/VentureHealthMap";
import { ConsensusQueue } from "@/components/uc/ConsensusQueue";
import { Command, Swords, Sunrise, Loader2, Crown, Archive, RotateCcw, Activity, Briefcase, CreditCard, Database, BrainCircuit } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { sweepApi, oracleApi } from "@/lib/api-client";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pulseKey, setPulseKey] = useState(0);
  const [alertKey, setAlertKey] = useState(0);
  const [taskKey, setTaskKey] = useState(0);
  const [fixKey, setFixKey] = useState(0);
  const [planKey, setPlanKey] = useState(0);
  const [warRoomOpen, setWarRoomOpen] = useState(false);
  const [opsOpen, setOpsOpen] = useState(false);
  const [opsFilter, setOpsFilter] = useState("active");
  const [sweeping, setSweeping] = useState(false);
  const [resetting, setResetting] = useState(false);

  const operatorReset = async () => {
    setResetting(true);
    toast.info("Sovereign Override — re-scanning estate memory and live API feeds…");
    try {
      const r = await oracleApi.operatorReset();
      const s = r.snapshot || {};
      const audit = s.conflict_audit || {};
      toast.success(
        `Operator Reset complete — ${(s.estate_memory || []).length} memory facts re-scanned, ` +
        `estate API ${s.sovereign_estate_api?.host_live ? "LIVE" : "unreachable"}, ` +
        `conflict audit: ${audit.gaps_found ?? 0} gap(s), ${audit.flags_filed ?? 0} flag(s) filed`);
      bump();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operator Reset failed — retry");
    } finally { setResetting(false); }
  };

  const bump = () => {
    setPulseKey((k) => k + 1);
    setAlertKey((k) => k + 1);
    setTaskKey((k) => k + 1);
    setFixKey((k) => k + 1);
    setPlanKey((k) => k + 1);
  };

  const runSweep = async () => {
    setSweeping(true);
    try {
      const r = await sweepApi.runNow();
      toast.success(`Sweep complete — ${r.agents_run ?? 7} agents deployed, ${r.insights_created ?? 0} new insights`);
      bump();
    } catch { toast.error("Sweep failed"); }
    finally { setSweeping(false); }
  };

  const centerName = user?.command_center_name || "Command OS";

  return (
    <div className="min-h-screen uc-grain relative" data-testid="dashboard">
      <Onboarding />
      {user?.onboarding_completed !== false && <CommandBriefing userId={user?.id} />}
      <WarRoom open={warRoomOpen} onOpenChange={setWarRoomOpen} onCreated={bump} />
      <MandateDrawer open={opsOpen} onOpenChange={setOpsOpen} initialFilter={opsFilter} />

      <div className="relative z-10 max-w-[1600px] mx-auto p-4 md:p-8">
        {/* Header */}
        <header className="mb-8 pb-2 border-b border-white/10 sticky top-0 z-30 backdrop-blur-md" style={{ background: "rgba(10,10,10,0.85)" }}>
          <div className="flex items-center justify-between pb-3">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center border border-white/15" style={{ background: "var(--uc-surface-2)" }}>
              <Command size={18} className="text-amber-400" />
            </div>
            <div>
              <div className="font-mono-uc text-[10px] uppercase tracking-[0.3em] text-amber-400/80" data-testid="sovereign-brand">THE SOVEREIGN UMBRELLA COMMAND</div>
              <div className="flex items-center gap-2 mt-0.5">
                <h1 className="font-serif-display text-2xl leading-none" data-testid="center-name">
                  {centerName}
                </h1>
                {user?.subscription_tier && (
                  <span className="font-mono-uc text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-400" data-testid="tier-badge">
                    {user.subscription_tier}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <OracleVoice />
            <button
              data-testid="operations-drawer-btn"
              onClick={() => { setOpsFilter("active"); setOpsOpen(true); }}
              className="hidden md:flex items-center gap-2 uc-chip hover:bg-emerald-500/10 hover:border-emerald-500/50 transition-colors uc-focus px-3 py-1.5"
            >
              <Activity size={11} className="text-emerald-300"/>
              <span className="text-emerald-300">Operations</span>
            </button>
            <button
              data-testid="boardroom-vault-btn"
              onClick={() => navigate("/vault")}
              className="hidden md:flex items-center gap-2 uc-chip hover:bg-amber-500/10 hover:border-amber-500/50 transition-colors uc-focus px-3 py-1.5"
            >
              <Archive size={11} className="text-amber-400"/>
              <span className="text-amber-400">Vault</span>
            </button>
            <button
              data-testid="arbitrage-boardroom-btn"
              onClick={() => navigate("/arbitrage")}
              className="hidden md:flex items-center gap-2 uc-chip hover:bg-sky-500/10 hover:border-sky-500/50 transition-colors uc-focus px-3 py-1.5"
            >
              <Briefcase size={11} className="text-sky-300"/>
              <span className="text-sky-300">Arbitrage</span>
            </button>
            <button
              data-testid="jarvis-bridge-btn"
              onClick={() => navigate("/jarvis")}
              className="hidden md:flex items-center gap-2 uc-chip hover:bg-sky-500/10 hover:border-sky-500/50 transition-colors uc-focus px-3 py-1.5"
            >
              <BrainCircuit size={11} className="text-sky-300"/>
              <span className="text-sky-300">Oracle Bridge</span>
            </button>
            <button
              data-testid="billing-nav-btn"
              onClick={() => navigate("/billing")}
              className="hidden md:flex items-center gap-2 uc-chip hover:bg-emerald-500/10 hover:border-emerald-500/50 transition-colors uc-focus px-3 py-1.5"
            >
              <CreditCard size={11} className="text-emerald-300"/>
              <span className="text-emerald-300">Billing</span>
            </button>
            {user?.is_root_admin && (
              <button
                data-testid="command-database-btn"
                onClick={() => navigate("/root/database")}
                className="hidden md:flex items-center gap-2 uc-chip hover:bg-rose-500/10 hover:border-rose-500/50 transition-colors uc-focus px-3 py-1.5"
              >
                <Database size={11} className="text-rose-300"/>
                <span className="text-rose-300">Database</span>
              </button>
            )}
            <button
              data-testid="directors-office-btn"
              onClick={() => navigate("/office")}
              className="hidden md:flex items-center gap-2 uc-chip hover:bg-amber-500/10 hover:border-amber-500/50 transition-colors uc-focus px-3 py-1.5"
            >
              <Crown size={11} className="text-amber-400"/>
              <span className="text-amber-400">Director's Office</span>
            </button>
            <button
              data-testid="run-sweep-btn"
              onClick={runSweep}
              disabled={sweeping}
              className="hidden md:flex items-center gap-2 uc-chip hover:bg-amber-500/10 hover:border-amber-500/50 transition-colors uc-focus px-3 py-1.5 disabled:opacity-50"
            >
              {sweeping ? <Loader2 size={11} className="animate-spin"/> : <Sunrise size={11} className="text-amber-400"/>}
              <span>Run Sweep</span>
            </button>
            <button
              data-testid="dashboard-operator-reset-btn"
              onClick={operatorReset}
              disabled={resetting}
              title="Sovereign Override — re-scan estate memory and all live API feeds to clear cognitive blind spots"
              className="hidden md:flex items-center gap-2 uc-chip hover:bg-rose-500/10 hover:border-rose-500/50 transition-colors uc-focus px-3 py-1.5 disabled:opacity-50"
            >
              {resetting ? <Loader2 size={11} className="animate-spin"/> : <RotateCcw size={11} className="text-rose-300"/>}
              <span className="text-rose-300">Operator Reset</span>
            </button>
            <button
              data-testid="open-war-room-btn"
              onClick={() => setWarRoomOpen(true)}
              className="hidden md:flex items-center gap-2 uc-chip hover:bg-red-500/10 hover:border-red-500/50 transition-colors uc-focus px-3 py-1.5"
            >
              <Swords size={11} className="text-red-400"/>
              <span className="text-red-400">War Room</span>
            </button>
            <div className="hidden md:flex items-center gap-2 uc-chip">
              <span className="uc-dot bg-emerald-400 uc-pulsing"></span> OPERATIONAL
            </div>
            <AlertsCenter refreshKey={alertKey} />
            <SettingsDrawer />
          </div>
          </div>
          {/* Zone Jump Bar — one-click navigation across Mission Control zones */}
          <nav className="flex items-center gap-1.5 overflow-x-auto uc-scroll" data-testid="zone-jump-bar">
            {[
              ["zone-pillar", "Pillar"],
              ["zone-operations", "Operations"],
              ["zone-drafts", "Drafts"],
              ["zone-revenue", "Revenue"],
              ["zone-control", "Control"],
              ["zone-leads", "Leads"],
            ].map(([id, label]) => (
              <button
                key={id}
                data-testid={`jump-${id}`}
                onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="font-mono-uc text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 border border-white/10 text-white/50 hover:text-amber-400 hover:border-amber-500/40 transition-colors flex-none"
              >
                {label}
              </button>
            ))}
          </nav>
        </header>

        {/* AUTONOMOUS OS — Boardroom landing: health map drives agent cadence,
            Consensus Queue is the always-visible central nervous system */}
        <div className="space-y-4 mb-6" data-testid="zone-autonomous-os">
          <VentureHealthMap refreshKey={pulseKey} />
          <ConsensusQueue refreshKey={taskKey} />
        </div>

        {/* Grid Layout — Modular Mission Control */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          <div className="lg:col-span-8 space-y-6">
            {/* THE PILLAR — Bloomberg density: Portfolio Health + Net Worth Pulse lead.
                Mandates + Victory Ledger live in the Operations Mandate Drawer (top nav). */}
            <ZoneDivider label="The Pillar" testid="zone-pillar" />
            <div className="uc-dense space-y-3" data-testid="pillar-dense-grid">
              <NetWorthPulse refreshKey={pulseKey} />
              <StatusHeatmap refreshKey={pulseKey} />
              <VentureCockpit onChanged={bump} />
              <ActionHub key={`hub-${taskKey}`} />
            </div>

            {/* OPERATIONS */}
            <ZoneDivider label="Operations" testid="zone-operations" />
            <FixPriorityQueue refreshKey={fixKey} />
            <TaskBoard refreshKey={taskKey} />

            {/* DRAFTS */}
            <ZoneDivider label="Drafts" testid="zone-drafts" />
            <DraftingTable refreshKey={taskKey} />

            {/* REVENUE ZONE — directly below Drafts */}
            <ZoneDivider label="Revenue Zone" testid="zone-revenue" />
            <RevenueWallet />
            <RevenueTerminal />

            {/* CONTROL CLUSTER — Run Command Console directly below System Optimizer */}
            <ZoneDivider label="Control Cluster" testid="zone-control" />
            <IntelligenceCore refreshKey={taskKey} />
            <ExecutionConsole />
            <BridgeHandshake />
            <WorkRateMonitor />
            <CreditLedgerPanel />
            <BattlePlanHistory refreshKey={planKey} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <AutoPilotPanel />
              <PrivateVault />
            </div>
            <CommandWallet />
            <InboxMonitor />
            <TwilioWire />

            {/* LEAD CONSOLE — bottom of the dashboard */}
            <ZoneDivider label="Lead Console" testid="zone-leads" />
            <LeadConsole refreshKey={taskKey} />
          </div>
          <div className="lg:col-span-4">
            <div className="sticky top-24 space-y-6 max-h-[calc(100vh-7rem)] overflow-y-auto uc-scroll pr-1">
              <CommandTerminal />
              <AgentBoardroom user={user} onAction={bump} refreshKey={taskKey} />
            </div>
          </div>
        </div>

        <BrandFooter />
        <div className="h-8" />
      </div>
      <UmbrellaAide />
      <GlobalLogTicker onOpenDrawer={(f) => { setOpsFilter(f || "active"); setOpsOpen(true); }} />
    </div>
  );
}

function ZoneDivider({ label, testid }) {
  return (
    <div id={testid} className="flex items-center gap-3 pt-3 first:pt-0 scroll-mt-32" data-testid={testid}>
      <div className="h-px w-8 bg-amber-500/50" />
      <span className="font-mono-uc text-[9px] uppercase tracking-[0.35em] text-white/40">{label}</span>
      <div className="flex-1 h-px bg-white/10" />
    </div>
  );
}
